import React from 'react';
import {
    Box,
    Button,
    Checkbox,
    Divider,
    FormControlLabel,
    IconButton,
    Link,
    ListSubheader,
    MenuItem,
    Popover,
    Skeleton,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import UndoIcon from '@mui/icons-material/Undo';
import LockIcon from '@mui/icons-material/Lock';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import CloseIcon from '@mui/icons-material/Close';
import { useTheme } from '@mui/material/styles';
import { useIntl } from 'react-intl';
import ReactMarkdown from 'react-markdown';
import TurndownService from 'turndown';
import BodyEditor from '../../BodyEditor';
import RichBodyEditor from '../../RichBodyEditor';
import { UI_PUBLISH_STATES } from '../../NewsQueueConstants';
import {
    ARTICLE_PAID_VALUES,
    ARTICLE_SECTION_VALUES,
    PUBLISH_STATES,
} from '../../../api/NewsQueueApiConstants';
import { USE_RICH_BODY_EDITOR } from '../constants';
import { getRoundedCurrentLocalDateTime } from '../utils';
import { useArticleView } from '../ArticleViewContext';
import EditableInlineField from './EditableInlineField';
import groupTargetMedias from './groupTargetMedias';
import SchedulePublishDialog from '../dialogs/SchedulePublishDialog';
import PublishConfirmDialog from '../dialogs/PublishConfirmDialog';
import ActionDialogShell from '../dialogs/ActionDialogShell';
import RejectDialog from '../dialogs/RejectDialog';

function formatSourceContent(rawValue) {
    const raw = String(rawValue ?? '');
    if (looksLikeHtml(raw)) {
        return {
            headerRows: [],
            body: raw.trim(),
            hasFormattedContent: false,
        };
    }
    const lines = raw.replace(/\r\n/g, '\n').split('\n');
    const headerRows = [];
    let index = 0;

    while (index < lines.length) {
        const line = lines[index];
        if (!line.trim()) {
            index += 1;
            break;
        }
        const separator = line.indexOf(':');
        if (separator <= 0) {
            break;
        }
        const label = line.slice(0, separator).trim();
        const value = line.slice(separator + 1).trim();
        if (!label || !value) {
            break;
        }
        headerRows.push({ label, value });
        index += 1;
    }

    const body = lines.slice(index).join('\n').trim();
    if (headerRows.length === 0) {
        return {
            headerRows: [],
            body: raw.trim(),
            hasFormattedContent: false,
        };
    }

    return {
        headerRows,
        body,
        hasFormattedContent: true,
    };
}

function looksLikeHtml(value) {
    const text = String(value ?? '').trim();
    if (!text) return false;
    return /<\/?[a-z][\s\S]*>/i.test(text);
}

function stripHtmlComments(value) {
    return String(value ?? '').replace(/<!--[\s\S]*?-->/g, '');
}

function extractHtmlFragment(value) {
    const text = String(value ?? '').trim();
    if (!text) return '';
    const firstTagMatch = text.match(/<\s*\/?\s*[a-z][^>]*>/i);
    if (!firstTagMatch || firstTagMatch.index == null) {
        return text;
    }
    return text.slice(firstTagMatch.index).trim();
}

function fallbackHtmlToText(value) {
    return stripHtmlComments(value)
        .replace(/<style[\s\S]*?<\/style>/gi, '')
        .replace(/<script[\s\S]*?<\/script>/gi, '')
        .replace(/<[^>]+>/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
}

function toImagePreviewSrc(value) {
    const text = String(value ?? '').trim();
    if (!text) return '';
    if (/^data:image\/[a-z0-9.+-]+;base64,/i.test(text)) {
        return text;
    }
    return `data:image/jpeg;base64,${text}`;
}

function fileToDataUrl(file) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result ?? ''));
        reader.onerror = () => reject(new Error('failed_to_read_file'));
        reader.readAsDataURL(file);
    });
}

function isSupportedImageFile(file) {
    if (!file) return false;

    const type = String(file.type ?? '').toLowerCase();
    if (type === 'image/png' || type === 'image/jpeg') {
        return true;
    }

    const name = String(file.name ?? '').toLowerCase();
    return name.endsWith('.png') || name.endsWith('.jpg') || name.endsWith('.jpeg');
}

function ErrorWarningBanner({ article }) {
    if (!article?.error_text) return null;
    const rawState = String(article?.publish_state ?? '').toLowerCase();
    const isError = rawState === PUBLISH_STATES.ERROR;
    const isWarning = rawState === PUBLISH_STATES.NEW;
    if (!isError && !isWarning) return null;
    return (
        <Stack
            direction="row"
            spacing={0.75}
            alignItems="flex-start"
            sx={{ mb: 1.5 }}
        >
            {isError ? (
                <ErrorIcon fontSize="small" color="error" sx={{ mt: '1px' }} />
            ) : (
                <WarningIcon fontSize="small" sx={{ color: 'warning.main', mt: '1px' }} />
            )}
            <Typography
                variant="caption"
                sx={{
                    color: isError ? 'error.main' : 'warning.main',
                    fontWeight: 400,
                    whiteSpace: 'pre-wrap',
                    paddingTop: '2px',
                }}
            >
                {article.error_text}
            </Typography>
        </Stack>
    );
}

export function TitleBlock({ showBack = false, onBack = null }) {
    const intl = useIntl();
    const {
        canEditFields,
        editRevertSavedLabel,
        editState,
        editableFields,
    } = useArticleView();
    const backLabel = intl.formatMessage({ id: 'newsQueue.common.back' });

    return (
        <Stack direction="row" alignItems="flex-start" spacing={1} sx={{ mb: 2.5 }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <EditableInlineField
                    blockClassName="editable-block-header"
                    containerSx={{ mb: 0 }}
                    typographyVariant="h5"
                    typographySx={{ fontWeight: 700, whiteSpace: 'pre-wrap' }}
                    canEdit={canEditFields}
                    value={editState.effectiveArticleHeader}
                    fallbackValue={intl.formatMessage({ id: 'newsQueue.article.defaultTitle' })}
                    isEdited={editState.hasSavedEdit(editableFields.HEADER)}
                    onChange={(nextValue) => {
                        editState.setTextField(editableFields.HEADER, nextValue);
                    }}
                    onRevert={() => editState.revertSavedField(editableFields.HEADER)}
                    revertTooltip={editRevertSavedLabel}
                    controlsClassName="edit-controls-header"
                />
            </Box>
            {showBack && typeof onBack === 'function' && (
                <Tooltip title={backLabel}>
                    <IconButton size="small" onClick={onBack} sx={{ mt: 0.25 }}>
                        <ChevronRightIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            )}
        </Stack>
    );
}

export default function ArticleViewBody({
    onExpandBody,
    compareLayout = false,
    originalBody = '',
    onBack,
}) {
    // TEMP (UI testing): set your own identifier here to force lock badge preview.
    // Remove this override after visual testing is complete.
    const TEST_LOCK_OWNER_OVERRIDE = null;

    const theme = useTheme();
    const intl = useIntl();
    const {
        article,
        isMobile,
        canEditFields,
        isLockedByOtherUser,
        publishState,
        ts,
        targetMedias,
        articleSources,
        isArticleBodyLoading,
        editStartLabel,
        editApplyLabel,
        editDiscardLabel,
        editRevertSavedLabel,
        editableFields,
        editState,
        configurationsQuery,
        acceptNews,
        declineNews,
        rejectionReasons,
        rejectState,
        publishStateUi,
        handlers,
        publishConfirmText,
        publishErrorText,
        availableMultiPublicationTargetMedias,
        defaultMultiPublicationTargetMedias,
    } = useArticleView();
    const [categoryAnchorEl, setCategoryAnchorEl] = React.useState(null);
    const [articlePaidAnchorEl, setArticlePaidAnchorEl] = React.useState(null);
    const [targetMediaAnchorEl, setTargetMediaAnchorEl] = React.useState(null);
    const [cancelRejectOpen, setCancelRejectOpen] = React.useState(false);
    const [selectedSource, setSelectedSource] = React.useState(null);
    const [showSourceRaw, setShowSourceRaw] = React.useState(false);
    const [imageDialogOpen, setImageDialogOpen] = React.useState(false);
    const [imageDialogData, setImageDialogData] = React.useState('');
    const [imageDialogCaption, setImageDialogCaption] = React.useState('');
    const [imageDialogPhotographer, setImageDialogPhotographer] = React.useState('');
    const [imageDialogDragActive, setImageDialogDragActive] = React.useState(false);
    const [imageDialogError, setImageDialogError] = React.useState('');
    const imageFileInputRef = React.useRef(null);
    const imageCaptionInputRef = React.useRef(null);

    // Compute effective multi-publication medias (article stored value, user edits, or defaults)
    const effectiveMultiPublicationTargetMedias = React.useMemo(() => {
        const articleMultiPub = editState.effectiveMultiPublicationTargetMedias;
        const hasSavedEdits = editState.hasSavedEdit(editableFields.MULTI_PUBLICATION_TARGET_MEDIAS);
        
        // If user has made edits, use the edited value
        if (hasSavedEdits) {
            return articleMultiPub;
        }
        
        // If article has stored multi-publication medias, use them
        if (articleMultiPub && articleMultiPub.length > 0) {
            return articleMultiPub;
        }
        
        // Otherwise use defaults
        return defaultMultiPublicationTargetMedias;
    }, [editState.effectiveMultiPublicationTargetMedias, editState.hasSavedEdit, defaultMultiPublicationTargetMedias, editableFields.MULTI_PUBLICATION_TARGET_MEDIAS]);

    const expandLabel = intl.formatMessage({ id: 'newsQueue.edit.expand' });
    const originalBodyLabel = intl.formatMessage({ id: 'newsQueue.body.originalLabel' });
    const sourceContentOpenLabel = intl.formatMessage({ id: 'newsQueue.article.sourceContent.open' });
    const sourceContentTitle = intl.formatMessage({ id: 'newsQueue.article.sourceContent.title' });
    const sourceContentBodyLabel = intl.formatMessage({ id: 'newsQueue.article.sourceContent.body' });
    const sourceContentShowRawLabel = intl.formatMessage({ id: 'newsQueue.article.sourceContent.showRaw' });
    const sourceContentShowFormattedLabel = intl.formatMessage({
        id: 'newsQueue.article.sourceContent.showFormatted',
    });
    const attachImageLabel = intl.formatMessage({
        id: 'newsQueue.article.image.attach',
    });
    const attachImageDialogTitle = intl.formatMessage({
        id: 'newsQueue.article.image.dialog.title',
    });
    const attachImageDropLabel = intl.formatMessage({
        id: 'newsQueue.article.image.dialog.drop',
    });
    const attachImagePickLabel = intl.formatMessage({
        id: 'newsQueue.article.image.dialog.pick',
    });
    const attachImageCaptionLabel = intl.formatMessage({
        id: 'newsQueue.article.image.caption',
    });
    const attachImagePhotographerLabel = intl.formatMessage({
        id: 'newsQueue.article.image.photographer',
    });
    const attachImageInvalidFileLabel = intl.formatMessage({
        id: 'newsQueue.article.image.invalidFile',
    });
    const attachImageInvalidFormatLabel = intl.formatMessage({
        id: 'newsQueue.article.image.invalidFormat',
    });
    const attachImageReadErrorLabel = intl.formatMessage({
        id: 'newsQueue.article.image.readError',
    });
    const attachImageApplyLabel = intl.formatMessage({
        id: 'newsQueue.article.image.apply',
    });
    const cancelLabel = intl.formatMessage({
        id: 'newsQueue.common.cancel',
    });
    const removeImageLabel = intl.formatMessage({
        id: 'newsQueue.article.image.remove',
    });
    const imageCreditLabel = intl.formatMessage({
        id: 'newsQueue.article.image.creditLabel',
    });
    const dateStr = ts.toLocaleDateString();
    const timeStr = ts.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    const BodyEditorComponent = USE_RICH_BODY_EDITOR ? RichBodyEditor : BodyEditor;
    const lockOwnerLabel = String(article?.locked_by ?? TEST_LOCK_OWNER_OVERRIDE ?? '');
    const lockOwnerDisplay = lockOwnerLabel.split('@')[0].trim();
    const showLockBanner = isLockedByOtherUser || Boolean(TEST_LOCK_OWNER_OVERRIDE);
    const targetMediaPickerOpen = Boolean(targetMediaAnchorEl);
    const getTargetMediaLabel = React.useCallback(
        (value) => {
            const key = String(value ?? '').trim();
            if (!key) return '-';
            // Use configured display name or fallback to the key itself
            return configurationsQuery?.data?.result?.media_name_by_target_media?.[key] ?? key;
        },
        [configurationsQuery?.data?.result?.media_name_by_target_media]
    );
    const targetMediaOptions = React.useMemo(() => {
        const currentValue = String(editState.effectiveTargetMedia ?? '').trim();
        const fromConfig = Array.isArray(targetMedias)
            ? targetMedias
                .filter((x) => typeof x === 'string')
                .map((x) => x.trim())
                .filter((x) => x.length > 0)
            : [];
        const next = Array.from(new Set(fromConfig));
        if (currentValue && !next.includes(currentValue)) {
            next.push(currentValue);
        }
        return next;
    }, [editState.effectiveTargetMedia, targetMedias]);
    const groupedTargetMedias = React.useMemo(
        () => groupTargetMedias(targetMediaOptions, getTargetMediaLabel),
        [targetMediaOptions, getTargetMediaLabel]
    );
    const handleTargetMediaChange = (targetMedia) => {
        const previousTargetMedia = String(editState.effectiveTargetMedia ?? '').trim();
        const nextTargetMedia = String(targetMedia ?? '').trim();
        const currentAuthor = String(editState.effectiveArticleAuthor ?? '').trim();
        const previousMediaLabel = getTargetMediaLabel(previousTargetMedia).trim();
        const shouldUpdateAuthor = currentAuthor.length > 0 &&
            (currentAuthor === previousTargetMedia || currentAuthor === previousMediaLabel);

        if (shouldUpdateAuthor && previousTargetMedia && nextTargetMedia && previousTargetMedia !== nextTargetMedia) {
            editState.setTextFields({
                [editableFields.TARGET_MEDIA]: targetMedia,
                [editableFields.AUTHOR]: getTargetMediaLabel(nextTargetMedia),
            });
        } else {
            editState.setTextField(editableFields.TARGET_MEDIA, targetMedia);
        }
    };
    const categoryPickerOpen = Boolean(categoryAnchorEl);
    const sourceContentDialogOpen = Boolean(selectedSource);
    const turndownService = React.useMemo(
        () =>
            new TurndownService({
                headingStyle: 'atx',
                bulletListMarker: '-',
                codeBlockStyle: 'fenced',
                emDelimiter: '_',
                strongDelimiter: '**',
            }),
        []
    );
    const selectedSourceContent = String(selectedSource?.content ?? '');
    const selectedSourceContentWithoutComments = React.useMemo(
        () => stripHtmlComments(selectedSourceContent),
        [selectedSourceContent]
    );
    const formattedSelectedSourceContent = React.useMemo(
        () => formatSourceContent(selectedSourceContentWithoutComments),
        [selectedSourceContentWithoutComments]
    );
    const sourceHasHtml = React.useMemo(
        () => looksLikeHtml(selectedSourceContentWithoutComments),
        [selectedSourceContentWithoutComments]
    );
    const selectedSourceHtmlFragment = React.useMemo(
        () => extractHtmlFragment(selectedSourceContentWithoutComments),
        [selectedSourceContentWithoutComments]
    );
    const sourceBodyHasHtml = React.useMemo(
        () => looksLikeHtml(formattedSelectedSourceContent.body),
        [formattedSelectedSourceContent.body]
    );
    const selectedSourceBodyHtmlFragment = React.useMemo(
        () => extractHtmlFragment(formattedSelectedSourceContent.body),
        [formattedSelectedSourceContent.body]
    );
    const selectedSourceMarkdown = React.useMemo(() => {
        if (!sourceHasHtml) return '';
        try {
            return turndownService.turndown(selectedSourceHtmlFragment).trim();
        } catch {
            return fallbackHtmlToText(selectedSourceHtmlFragment);
        }
    }, [selectedSourceHtmlFragment, sourceHasHtml, turndownService]);
    const selectedSourceBodyMarkdown = React.useMemo(() => {
        if (!sourceBodyHasHtml) return '';
        try {
            return turndownService.turndown(selectedSourceBodyHtmlFragment).trim();
        } catch {
            return fallbackHtmlToText(selectedSourceBodyHtmlFragment);
        }
    }, [selectedSourceBodyHtmlFragment, sourceBodyHasHtml, turndownService]);
    const canToggleSourceRaw = sourceHasHtml || formattedSelectedSourceContent.hasFormattedContent;
    const effectiveImageData = React.useMemo(
        () => String(editState.effectiveImageData ?? '').trim(),
        [editState.effectiveImageData]
    );
    const effectiveImagePreviewSrc = React.useMemo(
        () => toImagePreviewSrc(effectiveImageData),
        [effectiveImageData]
    );
    const effectiveCaption = String(editState.effectiveCaption ?? '').trim();
    const effectivePhotographer = String(editState.effectivePhotographer ?? '').trim();
    const hasImage = Boolean(effectiveImageData);
    const imageDialogHasSelection = String(imageDialogData ?? '').trim().length > 0;
    const imageDialogPreviewSrc = React.useMemo(
        () => toImagePreviewSrc(imageDialogData),
        [imageDialogData]
    );

    React.useEffect(() => {
        if (!imageDialogOpen || !imageDialogHasSelection) return;
        const timer = window.setTimeout(() => {
            imageCaptionInputRef.current?.focus();
        }, 0);
        return () => window.clearTimeout(timer);
    }, [imageDialogHasSelection, imageDialogOpen]);

    const resetImageDialogState = React.useCallback(() => {
        setImageDialogData('');
        setImageDialogCaption('');
        setImageDialogPhotographer('');
        setImageDialogDragActive(false);
        setImageDialogError('');
    }, []);

    const handleImageSelection = React.useCallback(
        async (file) => {
            if (!file) {
                setImageDialogError(attachImageInvalidFileLabel);
                return;
            }
            if (!isSupportedImageFile(file)) {
                setImageDialogError(attachImageInvalidFormatLabel);
                return;
            }

            try {
                const dataUrl = await fileToDataUrl(file);
                if (!dataUrl) {
                    setImageDialogError(attachImageReadErrorLabel);
                    return;
                }
                setImageDialogData(dataUrl);
                setImageDialogError('');
            } catch {
                setImageDialogError(attachImageReadErrorLabel);
            }
        },
        [attachImageInvalidFileLabel, attachImageInvalidFormatLabel, attachImageReadErrorLabel]
    );

    const openImageDialog = React.useCallback(
        ({ data = '', caption = '', photographer = '' } = {}) => {
            setImageDialogData(String(data ?? ''));
            setImageDialogCaption(String(caption ?? ''));
            setImageDialogPhotographer(String(photographer ?? ''));
            setImageDialogDragActive(false);
            setImageDialogError('');
            setImageDialogOpen(true);
        },
        []
    );
    const articlePaidPickerOpen = Boolean(articlePaidAnchorEl);
    const articlePaidOptions = React.useMemo(() => {
        const currentValue = String(editState.effectiveArticlePaid ?? '').trim();
        const next = [...ARTICLE_PAID_VALUES];
        if (currentValue && !next.includes(currentValue)) {
            next.push(currentValue);
        }
        return next;
    }, [editState.effectiveArticlePaid]);
    const categoryOptions = React.useMemo(() => {
        const currentValue = String(editState.effectiveArticleSection ?? '').trim();
        const next = [...ARTICLE_SECTION_VALUES];
        if (currentValue && !next.includes(currentValue)) {
            next.push(currentValue);
        }
        return next;
    }, [editState.effectiveArticleSection]);

    const dateTargetMediaRow = (
        <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            spacing={0}
            sx={{
                mb: 2,
                pb: 1,
                pt: compareLayout ? 0.5 : 0,
                borderBottom: compareLayout ? 'none' : '1px solid',
                borderColor: 'divider',
            }}
        >
            <Typography variant="body2" color="text.secondary" sx={{ width: '33%', textAlign: 'left' }}>
                {!compareLayout && typeof onExpandBody === 'function' && (
                    <Tooltip title={expandLabel}>
                        <IconButton
                            size="small"
                            onClick={onExpandBody}
                            sx={{ mr: 0.5, ml: -1.0 }}
                        >
                            <ChevronLeftIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                )}
                {dateStr} {timeStr}
            </Typography>
            <Typography
                variant="body2"
                color={showLockBanner ? 'error.main' : 'text.secondary'}
                sx={{
                    width: '34%',
                    textAlign: 'center',
                    fontWeight: showLockBanner ? 600 : 400,
                }}
            >
                {showLockBanner && (
                    <Stack
                        direction="row"
                        spacing={0.5}
                        alignItems="center"
                        justifyContent="center"
                        sx={{ minWidth: 0 }}
                    >
                        <LockIcon fontSize="small" color="error" />
                        <Typography
                            component="span"
                            variant="inherit"
                            color="inherit"
                            sx={{
                                minWidth: 0,
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                paddingTop: '2px',
                            }}
                        >
                            {lockOwnerDisplay}
                        </Typography>
                    </Stack>
                )}
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end" sx={{ width: '33%' }}>
                <Button
                    size="small"
                    variant="outlined"
                    onClick={(event) => setTargetMediaAnchorEl(event.currentTarget)}
                    disabled={!canEditFields || targetMediaOptions.length === 0}
                    sx={{ textTransform: 'none', borderColor: 'divider', fontWeight: 400 }}
                >
                    {getTargetMediaLabel(editState.effectiveTargetMedia)}
                </Button>
                {editState.hasSavedEdit(editableFields.TARGET_MEDIA) && (
                    <Tooltip title={editRevertSavedLabel}>
                        <IconButton
                            size="small"
                            onClick={() => editState.revertSavedField(editableFields.TARGET_MEDIA)}
                            disabled={!canEditFields}
                        >
                            <UndoIcon fontSize="inherit" />
                        </IconButton>
                    </Tooltip>
                )}
            </Stack>
        </Stack>
    );

    const topSection = (
        <>
            {!compareLayout && dateTargetMediaRow}

            <Popover
                open={targetMediaPickerOpen}
                anchorEl={targetMediaAnchorEl}
                onClose={() => setTargetMediaAnchorEl(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                PaperProps={{
                    sx: {
                        border: '1px solid',
                        borderColor: 'divider',
                        boxShadow: 1,
                        minWidth: 220,
                        maxHeight: 280,
                        overflowY: 'auto',
                    },
                }}
            >
                {groupedTargetMedias.flatMap(([letter, medias]) => [
                    <ListSubheader key={`group-${letter}`} component="div">{letter}</ListSubheader>,
                    ...medias.map(({ media, label }) => (
                        <MenuItem
                            key={media}
                            selected={media === editState.effectiveTargetMedia}
                            onClick={() => {
                                handleTargetMediaChange(media);
                                setTargetMediaAnchorEl(null);
                            }}
                        >
                            {label}
                        </MenuItem>
                    )),
                ])}
            </Popover>

            <ErrorWarningBanner article={article} />

            {!isMobile && <TitleBlock showBack={compareLayout} onBack={onBack} />}
        </>
    );

    const bodyContentSection = (
        <>
            {compareLayout && dateTargetMediaRow}

            {(canEditFields || editState.effectiveArticleIngress) && (
                <EditableInlineField
                    blockClassName="editable-block-ingress"
                    containerSx={{ mb: 1 }}
                    typographyVariant="subtitle1"
                    typographyColor="text.secondary"
                    typographySx={{ whiteSpace: 'pre-wrap' }}
                    canEdit={canEditFields}
                    value={editState.effectiveArticleIngress}
                    isEdited={editState.hasSavedEdit(editableFields.INGRESS)}
                    onChange={(nextValue) => {
                        editState.setTextField(editableFields.INGRESS, nextValue);
                    }}
                    onRevert={() => editState.revertSavedField(editableFields.INGRESS)}
                    revertTooltip={editRevertSavedLabel}
                    controlsClassName="edit-controls-ingress"
                />
            )}

            {!hasImage && canEditFields && (
                <Box sx={{ mb: 3, mt: 0 }}>
                    <Button
                        size="small"
                        variant="outlined"
                        startIcon={<AddPhotoAlternateIcon fontSize="small" />}
                        onClick={() => {
                            openImageDialog();
                        }}
                        sx={{ textTransform: 'none', borderColor: 'divider', fontWeight: 500 }}
                    >
                        {attachImageLabel}
                    </Button>
                </Box>
            )}

            {hasImage && (
                <Box sx={{ mb: 2.5 }}>
                    <Box
                        component="img"
                        src={effectiveImagePreviewSrc}
                        alt={effectiveCaption || intl.formatMessage({ id: 'newsQueue.article.image.alt' })}
                        onClick={() => {
                            if (!canEditFields) return;
                            openImageDialog({
                                data: editState.effectiveImageData,
                                caption: editState.effectiveCaption,
                                photographer: editState.effectivePhotographer,
                            });
                        }}
                        sx={{
                            width: '99%',
                            maxHeight: 420,
                            objectFit: 'cover',
                            borderRadius: 1,
                            border: '1px solid',
                            borderColor: 'divider',
                            cursor: canEditFields ? 'pointer' : 'default',
                        }}
                    />

                    {(effectiveCaption || effectivePhotographer) && (
                        <Typography
                            variant="caption"
                            onClick={() => {
                                if (!canEditFields) return;
                                openImageDialog({
                                    data: editState.effectiveImageData,
                                    caption: editState.effectiveCaption,
                                    photographer: editState.effectivePhotographer,
                                });
                            }}
                            sx={{
                                mt: 1,
                                display: 'block',
                                whiteSpace: 'pre-wrap',
                                cursor: canEditFields ? 'pointer' : 'default',
                            }}
                        >
                            {effectiveCaption && (
                                <Box component="span" sx={{ fontWeight: 700 }}>
                                    {effectiveCaption}
                                </Box>
                            )}
                            {effectiveCaption && effectivePhotographer && ' '}
                            {effectivePhotographer && (
                                <Box
                                    component="span"
                                    color="text.secondary"
                                    sx={{ fontWeight: 400, textTransform: 'uppercase' }}
                                >
                                    {imageCreditLabel}: {effectivePhotographer}
                                </Box>
                            )}
                        </Typography>
                    )}
                </Box>
            )}

            <EditableInlineField
                blockClassName="editable-block-author"
                containerSx={{ mb: 1 }}
                typographyVariant="body2"
                typographySx={{ whiteSpace: 'pre-wrap' }}
                label="Kirjoittaja:"
                labelSx={{ fontWeight: 600, mr: 0.5 }}
                contentSx={{ color: 'text.secondary' }}
                canEdit={canEditFields}
                value={editState.effectiveArticleAuthor}
                fallbackValue="-"
                isEdited={editState.hasSavedEdit(editableFields.AUTHOR)}
                onChange={(nextValue) => {
                    editState.setTextField(editableFields.AUTHOR, nextValue);
                }}
                onRevert={() => editState.revertSavedField(editableFields.AUTHOR)}
                revertTooltip={editRevertSavedLabel}
                controlsClassName="edit-controls-author"
            />

            <Box
                sx={{
                    mb: 1,
                    '& h1, & h2, & h3': { mt: 2.5 },
                    '& p': { my: 1.5 },
                    '& ul, & ol': { my: 1.5 },
                }}
            >
                {isArticleBodyLoading ? (
                    <>
                        <Skeleton variant="text" height={120} />
                        <Skeleton variant="text" height={80} />
                    </>
                ) : (
                    <Stack
                        direction={{ xs: 'column', lg: compareLayout ? 'row' : 'column' }}
                        spacing={2}
                        alignItems="stretch"
                    >
                        <Box sx={{ flex: compareLayout ? 1 : 'unset', minWidth: 0 }}>
                            <BodyEditorComponent
                                editableBodyField={editableFields.BODY}
                                effectiveArticleBody={editState.effectiveArticleBody}
                                editingField={editState.editingField}
                                editingValue={editState.editingValue}
                                alwaysEditable
                                canEditFields={canEditFields}
                                hasSavedEdit={editState.hasSavedEdit}
                                beginEdit={editState.beginEdit}
                                cancelEdit={editState.cancelEdit}
                                applyEdit={editState.applyEdit}
                                revertSavedField={editState.revertSavedField}
                                editStartLabel={editStartLabel}
                                editApplyLabel={editApplyLabel}
                                editDiscardLabel={editDiscardLabel}
                                editRevertSavedLabel={editRevertSavedLabel}
                                onEditingInput={(valueOrEvent) => {
                                    if (typeof valueOrEvent === 'string') {
                                        editState.setTextField(editableFields.BODY, valueOrEvent);
                                        return;
                                    }
                                    editState.setTextField(
                                        editableFields.BODY,
                                        valueOrEvent.currentTarget.textContent ?? ''
                                    );
                                }}
                            />
                        </Box>
                        {compareLayout && (
                            <Box
                                sx={{
                                    flex: 1,
                                    minWidth: 0,
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    borderRadius: 1,
                                    overflow: 'hidden',
                                    bgcolor: (theme.palette.grey && theme.palette.grey[50]) || '#f7f7f7',
                                }}
                            >
                                <Box
                                    sx={{
                                        minHeight: 41,
                                        px: 1.5,
                                        display: 'flex',
                                        alignItems: 'center',
                                        borderBottom: '1px solid',
                                        borderColor: 'divider',
                                        bgcolor: (theme.palette.grey && theme.palette.grey[100]) || '#f0f0f0',
                                    }}
                                >
                                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                        {originalBodyLabel}
                                    </Typography>
                                </Box>
                                <Box
                                    sx={{
                                        p: 1.5,
                                        '& p': { my: 1.5 },
                                        '& h1, & h2, & h3': { mt: 2.5 },
                                        '& ul, & ol': { my: 1.5 },
                                    }}
                                >
                                    <ReactMarkdown>{String(originalBody || '')}</ReactMarkdown>
                                </Box>
                            </Box>
                        )}
                    </Stack>
                )}
            </Box>

            <Stack direction="column" spacing={0} sx={{ m: 0, p: 0 }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 300 }}>
                        {intl.formatMessage({ id: 'newsQueue.article.department' })}:
                    </Typography>
                    <Button
                        size="small"
                        variant="outlined"
                        onClick={(event) => setCategoryAnchorEl(event.currentTarget)}
                        disabled={!canEditFields || categoryOptions.length === 0}
                        sx={{ textTransform: 'none', borderColor: 'divider', fontWeight: 400 }}
                    >
                        {editState.effectiveArticleSection || '-'}
                    </Button>
                    {editState.hasSavedEdit(editableFields.SECTION) && (
                        <Tooltip title={editRevertSavedLabel}>
                            <IconButton
                                size="small"
                                onClick={() => editState.revertSavedField(editableFields.SECTION)}
                                disabled={!canEditFields}
                            >
                                <UndoIcon fontSize="inherit" />
                            </IconButton>
                        </Tooltip>
                    )}
                </Stack>

                <Popover
                    open={articlePaidPickerOpen}
                    anchorEl={articlePaidAnchorEl}
                    onClose={() => setArticlePaidAnchorEl(null)}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                    PaperProps={{
                        sx: {
                            border: '1px solid',
                            borderColor: 'divider',
                            boxShadow: 1,
                            minWidth: 220,
                            maxHeight: 280,
                            overflowY: 'auto',
                        },
                    }}
                >
                    {articlePaidOptions.map((articlePaid) => (
                        <MenuItem
                            key={articlePaid}
                            selected={articlePaid === editState.effectiveArticlePaid}
                            onClick={() => {
                                editState.setTextField(editableFields.PAID, articlePaid);
                                setArticlePaidAnchorEl(null);
                            }}
                        >
                            {articlePaid}
                        </MenuItem>
                    ))}
                </Popover>

                <Popover
                    open={categoryPickerOpen}
                    anchorEl={categoryAnchorEl}
                    onClose={() => setCategoryAnchorEl(null)}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                    PaperProps={{
                        sx: {
                            border: '1px solid',
                            borderColor: 'divider',
                            boxShadow: 1,
                            minWidth: 220,
                            maxHeight: 280,
                            overflowY: 'auto',
                        },
                    }}
                >
                    {categoryOptions.map((category) => (
                        <MenuItem
                            key={category}
                            selected={category === editState.effectiveArticleSection}
                            onClick={() => {
                                editState.setTextField(editableFields.SECTION, category);
                                setCategoryAnchorEl(null);
                            }}
                        >
                            {category}
                        </MenuItem>
                    ))}
                </Popover>

                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 300 }}>
                        {intl.formatMessage({ id: 'newsQueue.article.articlePaid' })}:
                    </Typography>
                    <Button
                        size="small"
                        variant="outlined"
                        onClick={(event) => setArticlePaidAnchorEl(event.currentTarget)}
                        disabled={!canEditFields || articlePaidOptions.length === 0}
                        sx={{ textTransform: 'none', borderColor: 'divider', fontWeight: 400 }}
                    >
                        {editState.effectiveArticlePaid || '-'}
                    </Button>
                    {editState.hasSavedEdit(editableFields.PAID) && (
                        <Tooltip title={editRevertSavedLabel}>
                            <IconButton
                                size="small"
                                onClick={() => editState.revertSavedField(editableFields.PAID)}
                                disabled={!canEditFields}
                            >
                                <UndoIcon fontSize="inherit" />
                            </IconButton>
                        </Tooltip>
                    )}
                </Stack>
                <FormControlLabel
                    control={
                        <Checkbox
                            size="small"
                            checked={editState.effectiveAiClause}
                            disabled={!canEditFields}
                            sx={{ ml: 0, pl: 0, my: 0.5, py: 0.5 }}
                            onChange={(e) =>
                                editState.setBooleanField(editableFields.AI_CLAUSE, e.target.checked)
                            }
                        />
                    }
                    label={
                        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 300 }}>
                            {intl.formatMessage({ id: 'newsQueue.article.aiClause' })}
                        </Typography>
                    }
                    sx={{ m: 0 }}
                />
            </Stack>

            {(article.search ||
                (publishState === UI_PUBLISH_STATES.ACCEPTED && article.url)) && (
                    <>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                            {intl.formatMessage({ id: 'newsQueue.article.links' })}
                        </Typography>
                        <Box component="ul" sx={{ pl: 3, m: 0, mb: 3 }}>
                            {article.search && (
                                <Box component="li">
                                    <Typography variant="body2">
                                        <a href={article.search} target="_blank" rel="noreferrer">
                                            {intl.formatMessage({ id: 'newsQueue.article.link.context' })}
                                        </a>
                                    </Typography>
                                </Box>
                            )}
                            {publishState === UI_PUBLISH_STATES.ACCEPTED && article.url && (
                                <Box component="li">
                                    <Typography variant="body2">
                                        <a href={article.url} target="_blank" rel="noreferrer">
                                            {intl.formatMessage({ id: 'newsQueue.article.link.cms' })}
                                        </a>
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    </>
                )}

            <Divider sx={{ my: 2.5 }} />
            {articleSources.length > 0 && (
                <Box sx={{ mt: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                        Lähteet:
                    </Typography>
                    <Box component="ul" sx={{ pl: 2.5, m: 0 }}>
                        {articleSources.map((source, index) => (
                            <Box component="li" key={`${source.url ?? 'source'}-${index}`}>
                                {(source.url && source.url !== 'about:blank') && (
                                    <div>
                                        <Link
                                            href={source.url}
                                            target="_blank"
                                            rel="noreferrer"
                                            variant="body2"
                                        >
                                            <span>{source.urlName ? source.urlName : source.url}</span>
                                            <OpenInNewIcon
                                                aria-hidden="true"
                                                sx={{ fontSize: 14, ml: 0.5, verticalAlign: 'text-bottom', paddingBottom: "1px" }}
                                            />
                                        </Link>
                                    </div>
                                )}
                                {(!source.url || source.url === 'about:blank') && source.content && (
                                    <div>
                                        <Link
                                            component="button"
                                            type="button"
                                            variant="body2"
                                            underline="hover"
                                            onClick={() => {
                                                setSelectedSource(source);
                                                setShowSourceRaw(false);
                                            }}
                                        >
                                            {source.urlName ? source.urlName : sourceContentOpenLabel}
                                        </Link>
                                    </div>
                                )}
                                {source.description && (
                                    <Typography variant="caption" color="text.secondary">
                                        {source.description}
                                    </Typography>
                                )}
                            </Box>
                        ))}
                    </Box>
                </Box>
            )}
            {!compareLayout && <Divider sx={{ my: 2.5 }} />}

            <ActionDialogShell
                open={sourceContentDialogOpen}
                onClose={() => {
                    setSelectedSource(null);
                    setShowSourceRaw(false);
                }}
                title={selectedSource?.urlName || sourceContentTitle}
                dialogProps={{
                    fullWidth: true,
                    maxWidth: 'lg',
                    PaperProps: {
                        sx: {
                            width: { xs: '80vw', sm: '80vw' },
                        },
                    },
                }}
                actions={
                    <>
                        {canToggleSourceRaw && (
                            <Button
                                onClick={() => setShowSourceRaw((prev) => !prev)}
                            >
                                {showSourceRaw
                                    ? sourceContentShowFormattedLabel
                                    : sourceContentShowRawLabel}
                            </Button>
                        )}
                        <Button
                            onClick={() => {
                                setSelectedSource(null);
                                setShowSourceRaw(false);
                            }}
                        >
                            {intl.formatMessage({ id: 'newsQueue.common.close' })}
                        </Button>
                    </>
                }
            >
                {showSourceRaw || !formattedSelectedSourceContent.hasFormattedContent ? (
                    sourceHasHtml && !showSourceRaw ? (
                        <Box
                            sx={{
                                mt: 2,
                                maxHeight: 420,
                                overflowY: 'auto',
                                '& p': { my: 1 },
                                '& ul, & ol': { my: 1, pl: 3 },
                                '& h1, & h2, & h3, & h4': { mt: 1.5, mb: 0.5 },
                                '& pre': {
                                    p: 1,
                                    borderRadius: 1,
                                    bgcolor: 'grey.100',
                                    overflowX: 'auto',
                                },
                            }}
                        >
                            <ReactMarkdown>
                                {selectedSourceMarkdown || fallbackHtmlToText(selectedSourceHtmlFragment)}
                            </ReactMarkdown>
                        </Box>
                    ) : (
                        <Typography
                            variant="body2"
                            sx={{
                                mt: 2,
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-word',
                                maxHeight: 420,
                                overflowY: 'auto',
                                fontFamily: 'monospace',
                            }}
                        >
                            {selectedSourceContent}
                        </Typography>
                    )
                ) : (
                    <Stack spacing={1} sx={{ mt: 2, maxHeight: 420, overflowY: 'auto' }}>
                        {formattedSelectedSourceContent.headerRows.map((row) => (
                            <Stack key={row.label} direction="row" spacing={0.5}>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    {row.label}:
                                </Typography>
                                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                    {row.value}
                                </Typography>
                            </Stack>
                        ))}
                        {formattedSelectedSourceContent.body && (
                            <>
                                <Typography variant="body2" sx={{ fontWeight: 600, pt: 0.5 }}>
                                    {sourceContentBodyLabel}
                                </Typography>
                                {sourceBodyHasHtml ? (
                                    <Box
                                        sx={{
                                            '& p': { my: 1 },
                                            '& ul, & ol': { my: 1, pl: 3 },
                                            '& h1, & h2, & h3, & h4': { mt: 1.5, mb: 0.5 },
                                            '& pre': {
                                                p: 1,
                                                borderRadius: 1,
                                                bgcolor: 'grey.100',
                                                overflowX: 'auto',
                                            },
                                        }}
                                    >
                                        <ReactMarkdown>
                                            {selectedSourceBodyMarkdown || fallbackHtmlToText(formattedSelectedSourceContent.body)}
                                        </ReactMarkdown>
                                    </Box>
                                ) : (
                                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                        {formattedSelectedSourceContent.body}
                                    </Typography>
                                )}
                            </>
                        )}
                    </Stack>
                )}
            </ActionDialogShell>

            <ActionDialogShell
                open={imageDialogOpen}
                onClose={() => {
                    resetImageDialogState();
                    setImageDialogOpen(false);
                }}
                title={attachImageDialogTitle}
                headerBgColor={theme.palette.draft?.main}
                headerTextColor={theme.palette.draft?.contrastText}
                dialogProps={{
                    fullWidth: true,
                    maxWidth: 'sm',
                }}
                actions={
                    <>
                        <Button
                            onClick={() => {
                                resetImageDialogState();
                                setImageDialogOpen(false);
                            }}
                        >
                            {cancelLabel}
                        </Button>
                        <Button
                            variant="contained"
                            color="primary"
                            sx={{
                                bgcolor: theme.palette.draft?.main,
                                color: theme.palette.draft?.contrastText,
                                '&:hover': {
                                    bgcolor: theme.palette.draft?.main,
                                },
                            }}
                            disabled={!imageDialogHasSelection}
                            onClick={() => {
                                editState.setTextFields({
                                    [editableFields.IMAGE_DATA]: imageDialogData,
                                    [editableFields.CAPTION]: imageDialogCaption,
                                    [editableFields.PHOTOGRAPHER]: imageDialogPhotographer,
                                });
                                resetImageDialogState();
                                setImageDialogOpen(false);
                            }}
                        >
                            {attachImageApplyLabel}
                        </Button>
                    </>
                }
            >
                <Box sx={{ mt: 2 }}>
                    <input
                        ref={imageFileInputRef}
                        type="file"
                        accept=".png,.jpg,.jpeg,image/png,image/jpeg"
                        style={{ display: 'none' }}
                        onChange={(event) => {
                            const file = event.target.files?.[0];
                            handleImageSelection(file);
                            event.target.value = '';
                        }}
                    />

                    {!imageDialogHasSelection ? (
                        <Box
                            role="button"
                            tabIndex={0}
                            onClick={() => imageFileInputRef.current?.click()}
                            onKeyDown={(event) => {
                                if (event.key === 'Enter' || event.key === ' ') {
                                    event.preventDefault();
                                    imageFileInputRef.current?.click();
                                }
                            }}
                            onDragOver={(event) => {
                                event.preventDefault();
                                setImageDialogDragActive(true);
                            }}
                            onDragLeave={(event) => {
                                event.preventDefault();
                                setImageDialogDragActive(false);
                            }}
                            onDrop={(event) => {
                                event.preventDefault();
                                setImageDialogDragActive(false);
                                const file = event.dataTransfer.files?.[0];
                                handleImageSelection(file);
                            }}
                            sx={{
                                border: '2px dashed',
                                borderColor: imageDialogDragActive ? 'primary.main' : 'divider',
                                borderRadius: 1,
                                p: 2,
                                textAlign: 'center',
                                cursor: 'pointer',
                                transition: 'border-color 0.15s ease',
                            }}
                        >
                            <Typography variant="body2" sx={{ mb: 1 }}>
                                {attachImageDropLabel}
                            </Typography>
                            <Button
                                size="small"
                                variant="outlined"
                                onClick={(event) => {
                                    event.stopPropagation();
                                    imageFileInputRef.current?.click();
                                }}
                                sx={{ textTransform: 'none' }}
                            >
                                {attachImagePickLabel}
                            </Button>
                        </Box>
                    ) : (
                        <Stack spacing={1.5}>
                            <Box
                                sx={{
                                    position: 'relative',
                                    borderRadius: 1,
                                    overflow: 'hidden',
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    '&:hover .image-remove-button': {
                                        opacity: 1,
                                    },
                                }}
                            >
                                <Box
                                    component="img"
                                    src={imageDialogPreviewSrc}
                                    alt={attachImageDialogTitle}
                                    sx={{
                                        width: '100%',
                                        maxHeight: 280,
                                        objectFit: 'cover',
                                        display: 'block',
                                    }}
                                />
                                <Tooltip title={removeImageLabel}>
                                    <IconButton
                                        size="small"
                                        className="image-remove-button"
                                        onClick={() => {
                                            setImageDialogData('');
                                            setImageDialogCaption('');
                                            setImageDialogPhotographer('');
                                            setImageDialogError('');
                                        }}
                                        sx={{
                                            position: 'absolute',
                                            top: 8,
                                            right: 8,
                                            opacity: 0,
                                            transition: 'opacity 0.15s ease',
                                            bgcolor: 'rgba(0,0,0,0.65)',
                                            color: '#fff',
                                            '&:hover': {
                                                bgcolor: 'rgba(0,0,0,0.8)',
                                            },
                                        }}
                                    >
                                        <CloseIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                            <TextField
                                inputRef={imageCaptionInputRef}
                                fullWidth
                                size="small"
                                label={attachImageCaptionLabel}
                                value={imageDialogCaption}
                                onChange={(event) => setImageDialogCaption(event.target.value)}
                            />
                            <TextField
                                fullWidth
                                size="small"
                                label={attachImagePhotographerLabel}
                                value={imageDialogPhotographer}
                                onChange={(event) => setImageDialogPhotographer(event.target.value)}
                            />
                        </Stack>
                    )}

                    {imageDialogError && (
                        <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                            {imageDialogError}
                        </Typography>
                    )}
                </Box>
            </ActionDialogShell>
        </>
    );

    const actionsSection = (
        <>
            {publishState === UI_PUBLISH_STATES.REJECTED ||
                publishState === UI_PUBLISH_STATES.ACCEPTED ? (
                <Box>
                    {publishState === UI_PUBLISH_STATES.REJECTED && (
                        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 1 }}>
                            <Typography variant="body2" sx={{ color: 'error.main', fontWeight: 600 }}>
                                {intl.formatMessage({ id: 'newsQueue.state.rejected' })}
                            </Typography>
                            <Tooltip title={intl.formatMessage({ id: 'newsQueue.dialog.cancelReject.title' })}>
                                <IconButton
                                    size="small"
                                    color="error"
                                    disabled={declineNews.isPending || isLockedByOtherUser}
                                    onClick={() => setCancelRejectOpen(true)}
                                >
                                    <UndoIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </Stack>
                    )}
                    {publishState === UI_PUBLISH_STATES.ACCEPTED && (
                        <Typography variant="body2" sx={{ color: 'primary.main', fontWeight: 600 }}>
                            {intl.formatMessage({ id: 'newsQueue.article.approved' })}
                        </Typography>
                    )}
                    {article.feedback && (
                        <Typography variant="body2" sx={{ mb: 0.5 }}>
                            "{article.feedback}"
                        </Typography>
                    )}
                    {article.approver && (
                        <Typography variant="caption" color="text.secondary">
                            {article.approver}
                        </Typography>
                    )}
                </Box>
            ) : (
                <Stack direction="row" spacing={2.5} justifyContent="space-between">
                    <Button
                        variant="contained"
                        color="primary"
                        sx={{
                            bgcolor: theme.palette.publish?.main,
                            color: theme.palette.publish?.contrastText,
                            '&:hover': {
                                bgcolor: theme.palette.publish?.main,
                            },
                        }}
                        disabled={
                            acceptNews.isPending ||
                            configurationsQuery.isLoading ||
                            isArticleBodyLoading ||
                            isLockedByOtherUser
                        }
                        onClick={() => {
                            publishStateUi.setPublishReasonExtra('');
                            publishStateUi.setPublishScheduleOpen(false);
                            publishStateUi.setPublishScheduleLocal('');
                            publishStateUi.setPublishConfirmMode('now');
                            publishStateUi.setPublishConfirmOpen(true);
                        }}
                    >
                        {intl.formatMessage({ id: 'newsQueue.actions.publish' })}
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        sx={{
                            bgcolor: theme.palette.scheduled?.main,
                            color: theme.palette.scheduled?.contrastText,
                            '&:hover': {
                                bgcolor: theme.palette.scheduled?.main,
                            },
                        }}
                        disabled={
                            acceptNews.isPending ||
                            configurationsQuery.isLoading ||
                            isArticleBodyLoading ||
                            isLockedByOtherUser
                        }
                        onClick={() => {
                            publishStateUi.setPublishReasonExtra('');
                            publishStateUi.setPublishConfirmOpen(false);
                            publishStateUi.setPublishConfirmMode(null);
                            publishStateUi.setPublishScheduleLocal(getRoundedCurrentLocalDateTime());
                            publishStateUi.setPublishScheduleOpen(true);
                        }}
                    >
                        {intl.formatMessage({ id: 'newsQueue.actions.schedule' })}
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        sx={{
                            bgcolor: theme.palette.draft?.main,
                            color: theme.palette.draft?.contrastText,
                            '&:hover': {
                                bgcolor: theme.palette.draft?.main,
                            },
                        }}
                        disabled={
                            acceptNews.isPending ||
                            configurationsQuery.isLoading ||
                            isArticleBodyLoading ||
                            isLockedByOtherUser
                        }
                        onClick={() => {
                            publishStateUi.setPublishReasonExtra('');
                            publishStateUi.setPublishScheduleOpen(false);
                            publishStateUi.setPublishScheduleLocal('');
                            publishStateUi.setPublishConfirmMode('draft');
                            publishStateUi.setPublishConfirmOpen(true);
                        }}
                    >
                        {intl.formatMessage({ id: 'newsQueue.actions.draft' })}
                    </Button>
                    <Button
                        variant="outlined"
                        color="error"
                        disabled={declineNews.isPending || isArticleBodyLoading || isLockedByOtherUser}
                        onClick={() => {
                            const first = rejectionReasons?.[0] ?? 'other';
                            rejectState.setRejectReason(first);
                            rejectState.setRejectReasonExtra('');
                            rejectState.setRejectOpen(true);
                        }}
                    >
                        {intl.formatMessage({ id: 'newsQueue.actions.reject' })}
                    </Button>
                </Stack>
            )}
        </>
    );

    return (
        <Box
            sx={
                compareLayout
                    ? { height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column' }
                    : undefined
            }
        >
            {compareLayout ? (
                <Box
                    sx={{
                        position: 'sticky',
                        top: 0,
                        zIndex: 2,
                        bgcolor: 'background.paper',
                        pt: 1,
                    }}
                >
                    {topSection}
                </Box>
            ) : (
                topSection
            )}

            {compareLayout ? (
                <Box
                    sx={{
                        flex: 1,
                        minHeight: 0,
                        overflowY: 'auto',
                        pb: 2,
                        borderTop: '1px solid',
                        borderColor: 'divider',
                    }}
                >
                    {bodyContentSection}
                </Box>
            ) : (
                bodyContentSection
            )}

            {compareLayout ? (
                <Box
                    sx={{
                        position: 'sticky',
                        bottom: 0,
                        zIndex: 2,
                        bgcolor: 'background.paper',
                        borderTop: '1px solid',
                        borderColor: 'divider',
                        py: 1.5,
                    }}
                >
                    {actionsSection}
                </Box>
            ) : (
                actionsSection
            )}

            <SchedulePublishDialog
                open={publishStateUi.publishScheduleOpen}
                onClose={handlers.closeScheduleDialog}
                publishReasonExtra={publishStateUi.publishReasonExtra}
                setPublishReasonExtra={publishStateUi.setPublishReasonExtra}
                publishScheduleLocal={publishStateUi.publishScheduleLocal}
                setPublishScheduleLocal={publishStateUi.setPublishScheduleLocal}
                publishScheduleValid={publishStateUi.publishScheduleValid}
                isPending={acceptNews.isPending}
                isArticleBodyLoading={isArticleBodyLoading}
                isError={acceptNews.isError}
                errorText={publishErrorText}
                onSchedule={handlers.schedulePublish}
                targetMediaOptions={targetMediaOptions}
                onTargetMediaChange={handleTargetMediaChange}
                effectiveTargetMedia={editState.effectiveTargetMedia}
                effectiveMultiPublicationTargetMedias={effectiveMultiPublicationTargetMedias}
                availableMultiPublicationTargetMedias={availableMultiPublicationTargetMedias}
                canEditFields={canEditFields}
                setArrayField={editState.setArrayField}
                editableFields={editableFields}
                getTargetMediaLabel={getTargetMediaLabel}
            />

            <PublishConfirmDialog
                open={publishStateUi.publishConfirmOpen}
                onClose={handlers.closePublishConfirmDialog}
                publishConfirmMode={publishStateUi.publishConfirmMode}
                publishReasonExtra={publishStateUi.publishReasonExtra}
                setPublishReasonExtra={publishStateUi.setPublishReasonExtra}
                publishConfirmText={publishConfirmText}
                isPending={acceptNews.isPending}
                isArticleBodyLoading={isArticleBodyLoading}
                isError={acceptNews.isError}
                errorText={publishErrorText}
                onConfirm={handlers.confirmPublish}
                targetMediaOptions={targetMediaOptions}
                onTargetMediaChange={handleTargetMediaChange}
                effectiveTargetMedia={editState.effectiveTargetMedia}
                effectiveMultiPublicationTargetMedias={effectiveMultiPublicationTargetMedias}
                availableMultiPublicationTargetMedias={availableMultiPublicationTargetMedias}
                canEditFields={canEditFields}
                setArrayField={editState.setArrayField}
                editableFields={editableFields}
                getTargetMediaLabel={getTargetMediaLabel}
            />

            <RejectDialog
                open={rejectState.rejectOpen}
                onClose={() => rejectState.setRejectOpen(false)}
                rejectReason={rejectState.rejectReason}
                setRejectReason={rejectState.setRejectReason}
                rejectReasonExtra={rejectState.rejectReasonExtra}
                setRejectReasonExtra={rejectState.setRejectReasonExtra}
                rejectionReasons={rejectionReasons}
                isConfigLoading={configurationsQuery.isLoading}
                isPending={declineNews.isPending}
                isArticleBodyLoading={isArticleBodyLoading}
                isError={declineNews.isError}
                onReject={handlers.rejectArticle}
            />

            <ActionDialogShell
                open={cancelRejectOpen}
                onClose={() => setCancelRejectOpen(false)}
                title={intl.formatMessage({ id: 'newsQueue.dialog.cancelReject.title' })}
                headerBgColor={theme.palette.primary.main}
                headerTextColor="#ffffff"
                actions={
                    <>
                        <Button
                            disabled={declineNews.isPending}
                            onClick={() => setCancelRejectOpen(false)}
                        >
                            {intl.formatMessage({ id: 'newsQueue.common.cancel' })}
                        </Button>
                        <Button
                            variant="contained"
                            color="primary"
                            sx={{ color: '#ffffff' }}
                            disabled={declineNews.isPending}
                            onClick={() => {
                                declineNews.revertDecline({
                                    news: article,
                                    clearFeedback: true,
                                });
                                setCancelRejectOpen(false);
                            }}
                        >
                            {intl.formatMessage({ id: 'newsQueue.actions.cancelReject' })}
                        </Button>
                    </>
                }
            >
                <Typography variant="body1" sx={{ mt: 2 }}>
                    {intl.formatMessage({ id: 'newsQueue.dialog.cancelReject.content' })}
                </Typography>
            </ActionDialogShell>
        </Box>
    );
}
